import { ModalHeader } from '@components/App/Modal/components/ModalHeader'
import { Button } from '@octopus-do/octopus-storybook'
import { Clone } from '@octopus-do/octopus-storybook/icons'
import type { SeoFormValues } from '@store/reducers/seo/types'
import type React from 'react'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { GlobalActions } from '@/store'
import type { AppState, ModalProps } from '@/types'
import { CodeRow } from './components/CodeRow'
import css from './styles.module.css'

interface Props {
  url: string
}

const OpenGraphImageText: React.FC<Props> = ({ url }) => {
  if (!url) {
    return <>open graph image</>
  }

  return (
    <a href={url} download className={css.export_seo_as_code_link}>
      open graph image
    </a>
  )
}

export const ExportSeoAsCode: React.FC<ModalProps> = ({ onClose }) => {
  const dispatch = useDispatch()
  const form = useSelector<AppState, SeoFormValues>(({ seoForm }) => seoForm)

  const firstString = 'Copy the code into your website <head>.'

  const stringIndex = form.image ? form.image.value.lastIndexOf('/') + 1 : -1

  const imagePath =
    form.image && stringIndex > 0
      ? `${form.url}/assets/${form.image.value.slice(stringIndex)}`
      : ''

  const code = useMemo(() => {
    return `
        <!-- Primary Meta Tags -->
        <title>${form.title}</title>
        <meta name='title' content='${form.title}'>
        <meta name='description' content='${form.description}'>

        <!-- Open Graph / Facebook -->
        <meta property='og:type' content='website'>
        <meta property='og:url' content='${form.url}'>
        <meta property='og:title' content='${form.title}'>
        <meta property='og:description' content='${form.description}'>
        <meta property='og:image' content='${imagePath}'>

        <!-- Twitter -->
        <meta property='twitter:card' content='summary_large_image'>
        <meta property='twitter:url' content='${form.url}'>
        <meta property='twitter:title' content='${form.title}'>
        <meta property='twitter:description' content='${form.description}'>
        <meta property='twitter:image' content='${imagePath}'>`
  }, [form, imagePath])

  const handleCopyToClipboard = async () => {
    dispatch(GlobalActions.copy({ value: code }))

    sendGtmEvent(GtmTriggerName.SEO_COPY)
  }

  const graphImageUrl = form.image ? form.image?.value : ''

  return (
    <div className={css.export_seo_as_code}>
      <ModalHeader title="Export as code" onClose={onClose} />
      <div className={css.export_seo_as_code_body}>
        <p className={css.export_seo_as_code_description}>
          {firstString}
          <br />
          Be sure to upload your <OpenGraphImageText url={graphImageUrl} /> to
          your CMS or host.
        </p>

        <code className={css.export_seo_as_code_row}>
          <CodeRow {...form} imagePath={imagePath} />
        </code>

        <Button
          size="medium"
          label="Copy"
          left={Clone}
          onClick={handleCopyToClipboard}
        />
      </div>
    </div>
  )
}
